import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faCheck } from '@fortawesome/free-solid-svg-icons';
import CrudManager from '../../components/common/CrudManager';
import AlertService from '../../services/AlertService';
import Modal from '../../components/common/Modal';
import Spinner from '../../components/common/Spinner';
import { getCompanyId } from '../../utils/companyHelper';

const Alerts = () => {
  const { t } = useTranslation();

  const [box, setBox] = useState('inbox'); // inbox | sent
  const [boxVersion, setBoxVersion] = useState(0);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [recipientStatuses, setRecipientStatuses] = useState([]);
  const [isLoadingRecipientStatuses, setIsLoadingRecipientStatuses] = useState(false);

  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientsQuery, setRecipientsQuery] = useState('');
  const [selectedPeopleIds, setSelectedPeopleIds] = useState([]);
  const [composeTitle, setComposeTitle] = useState('');
  const [composeText, setComposeText] = useState('');
  const [composeSendEmail, setComposeSendEmail] = useState(false);
  const [composeFiles, setComposeFiles] = useState([]);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isRecipientDropdownOpen, setIsRecipientDropdownOpen] = useState(false);
  const recipientPickerRef = useRef(null);
  const attachmentsInputRef = useRef(null);

  const openAlert = async (row) => {
    if (!row) return;

    // Marcar como leído al abrir
    if (row.readFlag === false) {
      try {
        await AlertService.markAsRead(row.id);
        window.dispatchEvent(new CustomEvent('alertsUpdated'));
        setRefreshKey((k) => k + 1); // fuerza refresh del grid
      } catch (_) {
        // Silencioso: el modal puede abrir igual
      }
    }

    setSelectedAlert(row);
    setIsModalOpen(true);

    // Cargar destinatarios/estado de lectura
    setIsLoadingRecipientStatuses(true);
    try {
      const list = await AlertService.getRecipientsStatus(row.id);
      setRecipientStatuses(Array.isArray(list) ? list : []);
    } catch (_) {
      setRecipientStatuses([]);
    } finally {
      setIsLoadingRecipientStatuses(false);
    }
  };

  useEffect(() => {
    const handler = () => {
      // Al cerrar modal o refrescar grid, el CrudManager se encarga del fetch.
      // Aquí solo forzamos el refresh de contador en header.
      window.dispatchEvent(new CustomEvent('alertsUpdated'));
    };
    window.addEventListener('companyChanged', handler);
    return () => window.removeEventListener('companyChanged', handler);
  }, []);

  const openCompose = async () => {
    setIsComposeOpen(true);
    setRecipientsQuery('');
    setSelectedPeopleIds([]);
    setComposeTitle('');
    setComposeText('');
    setComposeSendEmail(false);
    setComposeFiles([]);
    setIsRecipientDropdownOpen(false);

    const companyId = getCompanyId();
    if (!companyId) {
      setRecipients([]);
      return;
    }

    setIsLoadingRecipients(true);
    try {
      const list = await AlertService.getRecipientsForCompany(companyId);
      const normalized = Array.isArray(list) ? list : [];
      // Solo destinatarios con usuario asociado (porque la alerta se asigna al usuario)
      setRecipients(normalized.filter((p) => p && p.peopleId && p.userId));
    } catch (_) {
      setRecipients([]);
    } finally {
      setIsLoadingRecipients(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const el = recipientPickerRef.current;
      if (!el) return;
      if (!el.contains(event.target)) {
        setIsRecipientDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleRecipient = (peopleId) => {
    setSelectedPeopleIds((prev) => (
      prev.includes(peopleId) ? prev.filter((id) => id !== peopleId) : [...prev, peopleId]
    ));
  };

  const removeRecipient = (peopleId) => {
    setSelectedPeopleIds((prev) => prev.filter((id) => id !== peopleId));
  };

  const removeComposeFile = (idx) => {
    setComposeFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const selectedRecipients = useMemo(() => {
    if (!selectedPeopleIds.length) return [];
    const byId = new Map(recipients.map((r) => [r.peopleId, r]));
    return selectedPeopleIds.map((id) => byId.get(id)).filter(Boolean);
  }, [recipients, selectedPeopleIds]);

  const filteredRecipients = useMemo(() => {
    const q = (recipientsQuery || '').trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter((p) => {
      const name = (p?.fullName || '').toLowerCase();
      return name.includes(q);
    });
  }, [recipients, recipientsQuery]);

  const recipientDropdownItems = useMemo(() => {
    const MAX = 60;
    return filteredRecipients.slice(0, MAX);
  }, [filteredRecipients]);

  const sendBulkAlert = async () => {
    const companyId = getCompanyId();
    if (!companyId) return;

    const title = (composeTitle || '').trim();
    const text = (composeText || '').trim();
    if (!title || !text || selectedPeopleIds.length === 0) return;

    setIsSending(true);
    try {
      if (composeFiles && composeFiles.length > 0) {
        await AlertService.createBulkWithFiles({
          companyId,
          recipientPeopleIds: selectedPeopleIds,
          title,
          text,
          sendEmail: composeSendEmail,
          files: composeFiles,
        });
      } else {
        await AlertService.createBulk({
          companyId,
          recipientPeopleIds: selectedPeopleIds,
          title,
          text,
          sendEmail: composeSendEmail,
        });
      }
      setIsComposeOpen(false);
      window.dispatchEvent(new CustomEvent('alertsUpdated'));
      setTimeout(() => window.dispatchEvent(new CustomEvent('alertsUpdated')), 600);
      setRefreshKey((k) => k + 1);
    } catch (_) {
      // El NotificationContext de CrudManager no aplica aquí; por ahora silencioso.
    } finally {
      setIsSending(false);
    }
  };

  const columns = useMemo(() => {
    const base = [
    {
      key: 'sentDate',
      label: 'alert.sentDate',
      sortable: true,
      className: 'grid-date',
      render: (row) => row.sentDate ? new Date(row.sentDate).toLocaleString() : ''
    },
    {
      key: 'dateFrom',
      label: 'alert.dateFrom',
      showInGrid: false,
      sortable: false,
      showInFilter: true,
      filterType: 'date',
      filterKey: 'dateFrom',
      filterPlaceholder: 'alert.dateFromPlaceholder',
      filterSize: 'small'
    },
    {
      key: 'dateTo',
      label: 'alert.dateTo',
      showInGrid: false,
      sortable: false,
      showInFilter: true,
      filterType: 'date',
      filterKey: 'dateTo',
      filterPlaceholder: 'alert.dateToPlaceholder',
      filterSize: 'small'
    },
    {
      key: 'title',
      label: 'alert.alertLabel',
      sortable: true,
      maxLengthInGrid: 80,
      showInFilter: true,
      filterPlaceholder: 'alert.alertLabel',
      filterSize: 'medium',
      render: (row) => row.title || ''
    },
    {
      key: 'text',
      label: 'alert.textField',
      sortable: false,
      maxLengthInGrid: 80,
      showInFilter: true,
      filterKey: 'text',
      filterPlaceholder: 'alert.textField',
      filterSize: 'medium',
      className: 'grid-ellipsis'
    },
    // Company omitida: redundante (siempre en la company activa)
    ];

    if (box === 'inbox') {
      base.unshift({
        key: 'readFlag',
        label: 'alert.read',
        sortable: false,
        className: 'alert-status-column',
        headerClassName: 'alert-status-column',
        render: (row) => (
          row.readFlag ? (
            <span title={t('alert.readLabel', { defaultValue: 'Leído' })} aria-label={t('alert.readLabel', { defaultValue: 'Leído' })}>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#16A34A' }} />
            </span>
          ) : (
            <span title={t('alert.new', { defaultValue: 'Nuevo' })} aria-label={t('alert.new', { defaultValue: 'Nuevo' })}>
              <FontAwesomeIcon icon={faCircle} style={{ color: 'var(--primary-color)' }} />
            </span>
          )
        )
      });
      base.splice(2, 0, {
        key: 'senderPeopleFullName',
        label: 'alert.sender',
        sortable: false,
        maxLengthInGrid: 40,
        className: 'grid-ellipsis grid-nowrap',
        headerClassName: 'grid-nowrap'
      });
      const unreadFilter = {
        key: 'unreadOnly',
        label: 'alert.onlyUnread',
        showInGrid: false,
        sortable: false,
        showInFilter: true,
        filterType: 'checkbox',
        filterKey: 'unreadOnly',
        filterSize: 'small'
      };
      // Debe ser el último filtro visible
      const lastFilterIdx = (() => {
        let idx = -1;
        base.forEach((c, i) => {
          if (c && (c.showInFilter ?? false)) {
            idx = i;
          }
        });
        return idx;
      })();
      if (lastFilterIdx >= 0) {
        base.splice(lastFilterIdx + 1, 0, unreadFilter);
      } else {
        base.push(unreadFilter);
      }
    }

    return base;
  }, [t, box]);

  const alertGridService = useMemo(() => ({
    getAll: (page, size, filters) =>
      AlertService.getAll(page, size, { ...filters, box }, { key: 'sentDate', direction: 'descending' }),
  }), [box]);

  return (
    <>
      <div className="tabs" style={{ marginBottom: 10 }}>
        <ul className="nav nav-tabs">
          <li className={box === 'inbox' ? 'active' : ''}>
            <a
              href="#alerts-inbox"
              onClick={(e) => {
                e.preventDefault();
                setBox('inbox');
                setBoxVersion((v) => v + 1);
              }}
              style={{ cursor: 'pointer' }}
            >
              {t('alert.inbox', { defaultValue: 'Recibidos' })}
            </a>
          </li>
          <li className={box === 'sent' ? 'active' : ''}>
            <a
              href="#alerts-sent"
              onClick={(e) => {
                e.preventDefault();
                setBox('sent');
                setBoxVersion((v) => v + 1);
              }}
              style={{ cursor: 'pointer' }}
            >
              {t('alert.sent', { defaultValue: 'Enviados' })}
            </a>
          </li>
        </ul>
      </div>

      <CrudManager
        key={`${refreshKey}-${boxVersion}`}
        service={alertGridService}
        columns={columns}
        entityName="alert"
        icon={faBell}
        formLayout="modal"
        canCreate={true}
        canEdit={true}
        canDelete={false}
        canFilter={true}
        initialFilters={{ unreadOnly: false }}
        onAddClick={openCompose}
        onEditClick={openAlert}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedAlert?.title || t('alert.title', { defaultValue: 'Alerta' })}
        icon={faBell}
      >
        {(isLoadingRecipientStatuses) && (
          <Spinner overlay={true} size="large" message={t('common.loading', { defaultValue: 'Cargando...' })} />
        )}

        <div className="alert-view">
          <div className="alert-view-line">
            <div className="alert-view-label">{t('alert.sentDate', { defaultValue: 'Fecha de envío' })}:</div>
            <div className="alert-view-value">
              {selectedAlert?.sentDate ? new Date(selectedAlert.sentDate).toLocaleString() : '-'}
            </div>
          </div>

          <div className="alert-view-line">
            <div className="alert-view-label">{t('alert.sender', { defaultValue: 'Remitente' })}:</div>
            <div className="alert-view-value">{selectedAlert?.senderPeopleFullName || '-'}</div>
          </div>

          <div className="alert-view-section">
            <div className="alert-view-section-title">{t('alert.recipients', { defaultValue: 'Destinatarios' })}:</div>
            {recipientStatuses.length === 0 ? (
              <div className="alert-view-muted">-</div>
            ) : (
              <ul className="alert-view-recipient-list">
                {recipientStatuses.map((r) => (
                  <li key={r.alertId || `${r.recipientPeopleId || 'p'}-${r.recipientPeopleFullName || ''}`} className="alert-view-recipient-item">
                    <span className="alert-view-recipient-name">{r.recipientPeopleFullName || '-'}</span>
                    <span className={`alert-view-recipient-status ${r.readFlag ? 'is-read' : 'is-unread'}`}>
                      {r.readFlag
                        ? `${t('alert.readLabel', { defaultValue: 'Leído' })}${r.readDate ? ` · ${new Date(r.readDate).toLocaleString()}` : ''}`
                        : t('alert.new', { defaultValue: 'Nuevo' })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="alert-view-section">
            <div className="alert-view-section-title">{t('alert.textField', { defaultValue: 'Texto del alerta' })}:</div>
            <div className="alert-view-text">{selectedAlert?.text || ''}</div>
          </div>

          <div className="alert-view-section">
            <AlertAttachments alert={selectedAlert} />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isComposeOpen}
        onClose={() => !isSending && setIsComposeOpen(false)}
        title={t('alert.compose', { defaultValue: 'Crear alerta' })}
        icon={faBell}
      >
        {(isLoadingRecipients || isSending) && (
          <Spinner overlay={true} size="large" message={t(isSending ? 'common.processing' : 'common.loading')} />
        )}

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span>{t('alert.recipients', { defaultValue: 'Destinatarios' })}</span>
            <span style={{ color: '#6c757d', fontSize: 12 }}>
              {t('alert.selectedCount', { defaultValue: '{{count}} seleccionado(s)', count: selectedPeopleIds.length })}
            </span>
          </label>

          {selectedRecipients.length > 0 && (
            <div className="alert-recipient-chips" style={{ marginBottom: 10 }}>
              {selectedRecipients.map((p) => (
                <span key={p.peopleId} className="alert-recipient-chip">
                  <span className="alert-recipient-chip-label">{p.fullName}</span>
                  <button
                    type="button"
                    className="alert-recipient-chip-remove"
                    onClick={() => removeRecipient(p.peopleId)}
                    aria-label={t('common.delete', { defaultValue: 'Eliminar' })}
                    title={t('common.delete', { defaultValue: 'Eliminar' })}
                    disabled={isSending}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <div ref={recipientPickerRef} className="alert-recipient-picker">
            <input
              type="text"
              className="form-control"
              placeholder={t('alert.searchPeople', { defaultValue: 'Buscar persona...' })}
              value={recipientsQuery}
              onChange={(e) => {
                setRecipientsQuery(e.target.value);
                setIsRecipientDropdownOpen(true);
              }}
              onFocus={() => setIsRecipientDropdownOpen(true)}
              onClick={() => setIsRecipientDropdownOpen(true)}
              disabled={isSending}
              autoComplete="off"
            />

            {isRecipientDropdownOpen && (
              <div className="alert-recipient-dropdown" role="listbox" aria-label={t('alert.recipients', { defaultValue: 'Destinatarios' })}>
                {filteredRecipients.length === 0 ? (
                  <div className="alert-recipient-empty">{t('common.noData')}</div>
                ) : (
                  <>
                    {recipientDropdownItems.map((p) => (
                      <button
                        key={p.peopleId}
                        type="button"
                        className="alert-recipient-option"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => toggleRecipient(p.peopleId)}
                        disabled={isSending}
                      >
                        <span className={`alert-recipient-check ${selectedPeopleIds.includes(p.peopleId) ? 'is-selected' : ''}`}>
                          ✓
                        </span>
                        <span className="alert-recipient-name">{p.fullName}</span>
                      </button>
                    ))}
                    {filteredRecipients.length > recipientDropdownItems.length && (
                      <div className="alert-recipient-hint">
                        {t('alert.moreResults', { defaultValue: 'Mostrando {{shown}} de {{total}}. Sigue escribiendo para filtrar.', shown: recipientDropdownItems.length, total: filteredRecipients.length })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>{t('alert.titleField', { defaultValue: 'Título' })}</label>
          <input
            type="text"
            className="form-control"
            value={composeTitle}
            onChange={(e) => setComposeTitle(e.target.value)}
            disabled={isSending}
          />
        </div>

        <div className="form-group">
          <label>{t('alert.textField', { defaultValue: 'Texto' })}</label>
          <textarea
            className="form-control"
            rows={4}
            value={composeText}
            onChange={(e) => setComposeText(e.target.value)}
            disabled={isSending}
          />
        </div>

        <div className="form-group">
          <label>{t('alert.attachments', { defaultValue: 'Adjuntos' })}</label>
          <input
            ref={attachmentsInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const list = Array.from(e.target.files || []);
              setComposeFiles((prev) => {
                const next = [...(prev || []), ...list];
                // Deduplicar por (name, size, lastModified) para evitar repetir el mismo archivo
                const seen = new Set();
                return next.filter((f) => {
                  const key = `${f?.name || ''}__${f?.size || 0}__${f?.lastModified || 0}`;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
              });
              // limpiar valor para permitir re-seleccionar el mismo fichero
              e.target.value = '';
            }}
            disabled={isSending}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => attachmentsInputRef.current && attachmentsInputRef.current.click()}
            disabled={isSending}
          >
            <i className="fa fa-paperclip"></i> {t('alert.selectAttachments', { defaultValue: 'Seleccionar adjuntos' })}
          </button>

          {composeFiles.length > 0 && (
            <div className="alert-attachments-selected">
              {composeFiles.map((f, idx) => (
                <span key={`${f?.name || 'file'}-${idx}`} className="alert-attachment-chip">
                  <span className="alert-attachment-chip-label">{f?.name || t('alert.attachment', { defaultValue: 'Adjunto' })}</span>
                  <button
                    type="button"
                    className="alert-attachment-chip-remove"
                    onClick={() => removeComposeFile(idx)}
                    aria-label={t('common.delete', { defaultValue: 'Eliminar' })}
                    title={t('common.delete', { defaultValue: 'Eliminar' })}
                    disabled={isSending}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="alert-compose-email-row">
          <input
            id="alert-send-email"
            type="checkbox"
            checked={composeSendEmail}
            onChange={(e) => setComposeSendEmail(e.target.checked)}
            disabled={isSending}
          />
          <label htmlFor="alert-send-email">
            {t('alert.sendEmail', { defaultValue: 'Enviar también por correo' })}
          </label>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setIsComposeOpen(false)} disabled={isSending}>
            <i className="fa fa-times"></i> {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={sendBulkAlert}
            disabled={isSending || selectedPeopleIds.length === 0 || !composeTitle.trim() || !composeText.trim()}
          >
            <i className="fa fa-check"></i> {t('common.create')}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Alerts;

const AlertAttachments = ({ alert }) => {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!alert?.id) return;
      setLoading(true);
      try {
        const list = await AlertService.getAttachments(alert.id);
        setAttachments(Array.isArray(list) ? list : []);
      } catch (_) {
        setAttachments([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [alert?.id]);

  const download = async (att) => {
    if (!att?.id) return;
    setDownloadError('');
    setDownloadingId(att.id);
    try {
      const { blob, filename } = await AlertService.downloadAttachment(att.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (filename && filename.trim()) || att.originalFilename || `attachment_${att.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (ex) {
      const msg = ex?.message || t('common.error', { defaultValue: 'Error' });
      setDownloadError(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        {t('alert.attachments', { defaultValue: 'Adjuntos' })}
      </div>
      {!attachments.length ? (
        <div className="alert-view-muted">-</div>
      ) : null}
      {downloadError ? (
        <div style={{ color: '#dc3545', marginBottom: 6, fontSize: 12 }}>{downloadError}</div>
      ) : null}
      {attachments.length > 0 ? (
      <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'none' }}>
        {attachments.map((att) => (
          <li key={att.id} style={{ marginBottom: 6 }}>
            <button
              type="button"
              className="alert-attachment-download"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => download(att)}
              disabled={downloadingId === att.id}
              title={t('common.download', { defaultValue: 'Descargar' })}
            >
              <i className="fa fa-download alert-attachment-download-icon"></i>
              <span>{att.originalFilename || `Attachment ${att.id}`}</span>
              {downloadingId === att.id ? (
                <span style={{ marginLeft: 8 }}>
                  <Spinner overlay={false} size="small" />
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
      ) : null}
    </div>
  );
};

